import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, XCircle, Loader2 } from 'lucide-react';

const StaffCtx = createContext(null);
export const useStaff = () => {
  const ctx = useContext(StaffCtx);
  if (!ctx) throw new Error('useStaff deve essere usato dentro <StaffProvider>');
  return ctx;
};

export function StaffProvider({ children }) {
  const [me, setMe] = useState(null);
  const [services, setServices] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [meRes, svc, zn] = await Promise.all([
        api.get('/staff/me'), api.get('/staff/services/public'), api.get('/staff/zones/public'),
      ]);
      setMe(meRes.data); setServices(svc.data); setZones(zn.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !me) {
    return (
      <AppShell title="Area Staff">
        <div className="flex h-[60vh] items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />Caricamento della tua area…
        </div>
      </AppShell>
    );
  }

  // Gate: staff sospeso o rifiutato -> messaggio dedicato (nessun accesso alle sezioni)
  const blocked = me && ['rejected', 'suspended'].includes(me.status);
  if (blocked) {
    const isRejected = me.status === 'rejected';
    return (
      <AppShell title="Area Staff">
        <div className="mx-auto max-w-xl" data-testid="staff-blocked">
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              {isRejected
                ? <XCircle className="h-12 w-12 text-destructive" />
                : <AlertTriangle className="h-12 w-12 text-amber-500" />}
              <h2 className="font-display text-xl font-semibold text-foreground">
                {isRejected ? 'Richiesta non approvata' : 'Account sospeso'}
              </h2>
              <p className="max-w-md text-sm text-muted-foreground">
                {isRejected
                  ? "La tua richiesta di accesso come Staff non è stata approvata. Per maggiori informazioni contatta il nostro team all'indirizzo staff@progettosila.it."
                  : "Il tuo account Staff è temporaneamente sospeso. Contatta il nostro team per riattivarlo."}
              </p>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  return (
    <StaffCtx.Provider value={{ me, services, zones, reload: load }}>
      {children}
    </StaffCtx.Provider>
  );
}
